import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const LoginPage = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({
    user: '',
    passw: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  // Detectar autopreenchimento do navegador
  useEffect(() => {
    const checkAutofill = () => {
      const userInput = document.getElementById('user');
      const passInput = document.getElementById('passw');
      
      if (userInput && passInput) {
        const hasUser = userInput.value.trim() !== '';
        const hasPass = passInput.value.trim() !== '';
        
        if (hasUser && hasPass) {
          setCredentials({
            user: userInput.value,
            passw: passInput.value
          });
          setIsFormValid(true);
        }
      }
    };

    // Verificar imediatamente
    checkAutofill();
    
    // Verificar após um pequeno delay para autopreenchimento
    const timer = setTimeout(checkAutofill, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Atualizar validação quando credentials mudam
  useEffect(() => {
    setIsFormValid(credentials.user.trim() !== '' && credentials.passw.trim() !== '');
  }, [credentials]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!credentials.user.trim() || !credentials.passw.trim()) {
      toast.error('⚠️ Por favor, preencha todos os campos');
      return;
    }

    setIsLoading(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || `http://172.16.10.45:5040`;
      
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Armazenar token e dados do usuário no localStorage
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', data.user || credentials.user);
        localStorage.setItem('userName', data.name || credentials.user);
        
        
        setTimeout(() => {
          onLogin(data.token);
        }, 500);
      } else {
        // Mensagens de erro específicas por código HTTP
        if (response.status === 401) {
          toast.error('❌ Usuário ou senha incorretos');
        } else if (response.status === 403) {
          toast.error('❌ Acesso negado.');
        } else if (response.status === 500) {
          toast.error('⚠️ Erro no servidor. Tente novamente.');
        } else {
          toast.error('❌ Erro ao fazer login: ' + (data.detail || 'Tente novamente'));
        }
      }
    } catch (error) {
      toast.error('🔌 Erro de conexão. Verifique sua internet e tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 relative overflow-hidden bg-white py-8">
      
      {/* Elementos decorativos de fundo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-orange-600 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-orange-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>
      </div>

      <div className="max-w-sm w-full space-y-6 relative z-10 animate-fade-in">
        {/* Logo e Header */}
        <div className="text-center">
          <div className="flex items-center justify-center mb-4 animate-slide-down">
            <img src="cedisa-logo.png" alt="Cedisa Logo" className="h-10" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2 animate-slide-down animation-delay-200">
            Portal de Solicitação de Recursos
          </h2>
          <p className="text-orange-500 text-base font-semibold animate-slide-down animation-delay-400">
            Departamento de TI • CEDISA
          </p>
        </div>

        {/* Card de Login */}
        <div className="bg-gray-50 rounded-2xl shadow-xl p-6 space-y-5 border border-gray-200 animate-slide-up animation-delay-600">
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Campo Usuário */}
            <div className="space-y-2">
              <label htmlFor="user" className="block text-sm font-semibold text-gray-700">
                Usuário
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                  <svg className="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                </div>
                <input
                  id="user"
                  name="user"
                  type="text"
                  required
                  value={credentials.user}
                  onChange={handleInputChange}
                  className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 text-sm"
                  placeholder="Digite seu usuário"
                  disabled={isLoading}
                  autoComplete="username"
                />
              </div>
            </div>

            {/* Campo Senha */}
            <div className="space-y-2">
              <label htmlFor="passw" className="block text-sm font-semibold text-gray-700">
                Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                  <svg className="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM12 17c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM15.1 8H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z"/>
                  </svg>
                </div>
                <input
                  id="passw"
                  name="passw"
                  type={showPassword ? "text" : "password"}
                  required
                  value={credentials.passw}
                  onChange={handleInputChange}
                  className="block w-full pl-9 pr-10 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 text-sm"
                  placeholder="Digite sua senha"
                  disabled={isLoading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors duration-200 w-8"
                  tabIndex="-1"
                  title={showPassword ? "Ocultar senha" : "Mostrar senha"}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    {showPassword ? (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zM11.84 9.02l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5C21.27 7.61 17 4.5 12 4.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zM12 9c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                      </svg>
                    )}
                  </div>
                </button>
              </div>
            </div>

            {/* Botão de Login */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent rounded-xl text-white font-semibold text-sm transition-all duration-200 transform hover:scale-[1.02] disabled:transform-none disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                style={{
                  background: isLoading 
                    ? '#9CA3AF' 
                    : 'linear-gradient(135deg, #E85C1E 0%, #ff7a3d 100%)',
                }}
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Entrando...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <svg className="w-5 h-5 mr-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                    Entrar no Sistema
                  </span>
                )}
              </button>
            </div>
          </form>

          {/* Footer do Card */}
          <div className="pt-3 border-t border-gray-200">
            <div className="flex items-center justify-center text-xs text-gray-500">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Conexão segura e criptografada
            </div>
            <p className="text-center text-xs text-gray-500 mt-2">
              Em caso de problemas, entre em contato com a TI
            </p>
          </div>
        </div>
      </div>

      {/* CSS para animações */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        
        .animate-slide-down {
          animation: slide-down 0.6s ease-out;
        }
        
        .animate-slide-up {
          animation: slide-up 0.6s ease-out;
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animation-delay-200 {
          animation-delay: 0.2s;
          animation-fill-mode: both;
        }
        
        .animation-delay-400 {
          animation-delay: 0.4s;
          animation-fill-mode: both;
        }
        
        .animation-delay-600 {
          animation-delay: 0.6s;
          animation-fill-mode: both;
        }
        
        /* Garantir que a animação de spin funcione */
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        .animate-spin {
          animation: spin 1s linear infinite !important;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default LoginPage;